import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X, Filter } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import PlaceCard from "@/components/PlaceCard";

export default function PlacesList() {
  const { t, tx } = useI18n();
  const [params, setParams] = useSearchParams();
  const [places, setPlaces] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const q = params.get("q") || "";
  const category = params.get("category") || "";

  useEffect(() => { api.get("/categories").then(r => setCategories(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const opts = {};
    if (q) opts.q = q;
    if (category) opts.category = category;
    api.get("/places", { params: opts })
      .then(r => setPlaces(r.data))
      .catch(() => setPlaces([]))
      .finally(() => setLoading(false));
  }, [q, category]);

  const setFilter = (key, val) => {
    const p = new URLSearchParams(params);
    if (val) p.set(key, val); else p.delete(key);
    setParams(p);
  };

  const activeCat = categories.find(c => c.id === category);

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 md:py-16">
      <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">{t.nav.places}</h1>

      {/* Search + filters */}
      <div className="mt-6 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <input
            type="text"
            placeholder={t.hero.searchPh}
            defaultValue={q}
            onKeyDown={e => { if (e.key === "Enter") setFilter("q", e.currentTarget.value); }}
            data-testid="places-search-input"
            className="w-full pl-11 pr-4 py-3 rounded-full bg-muted outline-none focus:ring-2 ring-[hsl(var(--primary-red))]"
          />
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <button
          onClick={() => setFilter("category", "")}
          data-testid="filter-all"
          className={`px-4 py-2 rounded-full text-sm font-medium transition ${
            !category ? "bg-foreground text-white" : "bg-muted hover:bg-muted/70"
          }`}
        >
          {t.common.viewAll}
        </button>
        {categories.map(c => (
          <button
            key={c.id}
            onClick={() => setFilter("category", c.id)}
            data-testid={`filter-cat-${c.type}`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              category === c.id
                ? c.color === "red" ? "bg-[hsl(var(--primary-red))] text-white" : "bg-[hsl(var(--primary-green))] text-white"
                : "bg-muted hover:bg-muted/70"
            }`}
          >
            {tx(c.name)}
          </button>
        ))}
        {(q || category) && (
          <button
            onClick={() => { setParams({}); }}
            className="px-4 py-2 rounded-full text-sm font-medium border border-border hover:bg-muted flex items-center gap-1"
          >
            <X className="w-3.5 h-3.5" /> {t.common.clear}
          </button>
        )}
      </div>

      {activeCat && (
        <p className="mt-6 text-muted-foreground">{tx(activeCat.description)}</p>
      )}

      <div className="mt-8">
        {loading ? (
          <p className="text-muted-foreground" data-testid="places-loading">{t.common.loading}</p>
        ) : places.length === 0 ? (
          <p className="text-muted-foreground py-16 text-center" data-testid="places-empty">{t.common.noResults}</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="places-grid">
            {places.map(p => <PlaceCard key={p.id} place={p} />)}
          </div>
        )}
      </div>
    </div>
  );
}
